// generator.js — "Build today's workout": a pure, deterministic session builder.
//
// Same inputs → same workout (no Math.random, no clock reads beyond `now`).
// Everything it says comes from the user's own data (analytics.js):
//   - per-muscle recovery: hours since the last hard sets, scaled by how many
//     sets were done and how hard (RPE)
//   - this week's hard sets per muscle vs. its weekly target (VOLUME_LANDMARKS)
//   - e1RMs and recent top sets → suggested loads at a target RPE, rounded to
//     loads the user can actually make (plates, dumbbells, stacks)
//   - equipment profile, avoided exercises and injuries (state.profile)
//   - a time budget, with set + rest + warm-up + changeover time math
// Exercises with no history never get an invented load: they are marked
// "first time — find a weight at RPE 7".
//
//   generateWorkout(state, opts, now) → plan (see bottom of file for shape)
//   alternativesFor(state, plan, exerciseId, opts, now) → ranked swaps
//   planToSlots(plan) → { slots, exercises } for actions.ensureExercises + updateSessionSlots
//   muscleStatus(state, opts, now) → { [muscle]: { recovery, hoursSince, weekSets, target, deficit } }
//   resolveEquipment(profile) → equipment tags
//   estimateSeconds(items) → total session time in seconds

import {
  completedSessions, exerciseHistory, isWorkingSet, musclesFor, indexExercises, loadFor, e1rmRPE,
  VOLUME_LANDMARKS, weekStart, readiness,
} from './analytics.js';

const HOUR = 3600000;
const DAY = 24 * HOUR;
const KG = 0.45359237;

// ——— Vocabulary ————————————————————————————————————————————————————————————

export const GEN_MUSCLES = ['Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core'];
const LARGE = new Set(['Chest', 'Back', 'Quads', 'Hamstrings', 'Glutes']);
const UPPER = new Set(['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps']);
const LOWER = new Set(['Quads', 'Hamstrings', 'Glutes', 'Calves']);

export const TIME_OPTIONS = [20, 30, 45, 60, 75];
export const GOAL_OPTIONS = [
  { id: 'strength', label: 'Strength' },
  { id: 'hypertrophy', label: 'Muscle' },
  { id: 'general', label: 'General' },
];
export const FOCUS_OPTIONS = [
  { id: 'auto', label: 'Smart pick', muscles: GEN_MUSCLES, sub: 'Most recovered, most behind' },
  { id: 'full', label: 'Full body', muscles: GEN_MUSCLES },
  { id: 'upper', label: 'Upper', muscles: ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps'] },
  { id: 'lower', label: 'Lower', muscles: ['Quads', 'Hamstrings', 'Glutes', 'Calves', 'Core'] },
  { id: 'push', label: 'Push', muscles: ['Chest', 'Shoulders', 'Triceps'] },
  { id: 'pull', label: 'Pull', muscles: ['Back', 'Biceps', 'Shoulders'] },
  { id: 'legs', label: 'Legs', muscles: ['Quads', 'Hamstrings', 'Glutes', 'Calves'] },
  { id: 'muscles', label: 'Pick muscles', muscles: [] },
];

export const EQUIPMENT_TAGS = [
  { id: 'barbell', label: 'Barbell + rack' },
  { id: 'dumbbell', label: 'Dumbbells' },
  { id: 'bench', label: 'Bench' },
  { id: 'cable', label: 'Cables' },
  { id: 'machine', label: 'Machines' },
  { id: 'pullup', label: 'Pull-up bar' },
  { id: 'kettlebell', label: 'Kettlebell' },
  { id: 'bands', label: 'Bands' },
];
export const EQUIPMENT_PRESETS = [
  { id: 'gym', label: 'Full gym', sub: 'Barbells, dumbbells, cables, machines', tags: ['barbell', 'dumbbell', 'bench', 'cable', 'machine', 'pullup', 'kettlebell'] },
  { id: 'dumbbells', label: 'Home dumbbells', sub: 'Dumbbells and a bench', tags: ['dumbbell', 'bench'] },
  { id: 'bodyweight', label: 'Bodyweight', sub: 'No equipment at all', tags: [] },
  { id: 'custom', label: 'Custom', sub: 'Pick exactly what you have', tags: null },
];

// Profile equipment comes in two spellings: onboarding tags ('barbell', 'pullup')
// and the settings list ('Barbell', 'Smith', 'Bodyweight'). Normalise to tags.
export function resolveEquipment(profile) {
  const p = profile || {};
  const preset = EQUIPMENT_PRESETS.find(x => x.id === p.equipmentPreset);
  if (preset && preset.tags) return [...preset.tags];
  const list = Array.isArray(p.equipment) ? p.equipment : null;
  if (!list) return EQUIPMENT_PRESETS[0].tags.slice(); // unknown → assume a gym (most users)
  const out = new Set();
  let legacy = false;
  for (const raw of list) {
    const k = String(raw).toLowerCase().trim();
    if (raw !== k) legacy = true;
    if (k === 'smith') out.add('machine');
    else if (k === 'bodyweight' || k === 'none') { /* nothing to add */ }
    else if (k === 'band') out.add('bands');
    else if (k === 'pull-up bar' || k === 'pullup bar') out.add('pullup');
    else out.add(k);
  }
  // The settings list has no bench / pull-up bar entries: infer the obvious ones.
  if (legacy) {
    if (out.has('barbell') || out.has('dumbbell')) out.add('bench');
    if (out.has('barbell') && (out.has('machine') || out.has('cable'))) out.add('pullup');
  }
  return EQUIPMENT_TAGS.map(t => t.id).filter(t => out.has(t));
}
export function equipmentPresetOf(profile) {
  const p = profile || {};
  if (p.equipmentPreset && EQUIPMENT_PRESETS.some(x => x.id === p.equipmentPreset)) return p.equipmentPreset;
  const tags = resolveEquipment(p).sort().join();
  const hit = EQUIPMENT_PRESETS.find(x => x.tags && x.tags.slice().sort().join() === tags);
  return hit ? hit.id : 'custom';
}

// Joint stress tags the injury text maps onto.
const INJURY_WORDS = [
  [/lower back|low back|lumbar|spine|disc/i, 'lowerBack'],
  [/knee/i, 'knees'],
  [/shoulder|rotator/i, 'shoulders'],
  [/wrist/i, 'wrists'],
  [/elbow/i, 'elbows'],
  [/hip/i, 'hips'],
  [/neck/i, 'neck'],
];
export function injuryTags(text) {
  const t = String(text || '');
  return INJURY_WORDS.filter(([re]) => re.test(t)).map(([, tag]) => tag);
}
export const INJURY_LABEL = { lowerBack: 'lower back', knees: 'knees', shoulders: 'shoulders', wrists: 'wrists', elbows: 'elbows', hips: 'hips', neck: 'neck' };

// ——— Exercise library ————————————————————————————————————————————————————————
// Curated, well-known movements with explicit equipment needs. Ids are the app's
// built-in x_* ids where one exists (so history lines up), else catalog fx_* ids,
// else generator-only xg_* ids. tier 1 = main barbell lift, 2 = compound, 3 = isolation.
// needs: every tag required; any: at least one of these tags.

function G(id, name, group, secondary, equipment, needs, tier, pattern, extra = {}) {
  return { id, name, group, primary: [group], secondary, equipment, needs, tier, pattern, mechanic: tier === 3 ? 'isolation' : 'compound', stress: [], ...extra };
}
export const GEN_LIBRARY = [
  // Chest
  G('x_bench', 'Barbell Bench Press', 'Chest', ['Shoulders', 'Triceps'], 'Barbell', ['barbell', 'bench'], 1, 'hpush', { stress: ['shoulders'], technical: true }),
  G('fx_Barbell_Incline_Bench_Press_-_Medium_Grip', 'Incline Barbell Bench Press', 'Chest', ['Shoulders', 'Triceps'], 'Barbell', ['barbell', 'bench'], 2, 'ipush', { stress: ['shoulders'] }),
  G('x_flatdb', 'Flat DB Bench Press', 'Chest', ['Shoulders', 'Triceps'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'hpush'),
  G('fx_Incline_Dumbbell_Press', 'Incline Dumbbell Press', 'Chest', ['Shoulders', 'Triceps'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'ipush'),
  G('x_incsmith', 'Incline Smith Press', 'Chest', ['Shoulders', 'Triceps'], 'Smith', ['machine'], 2, 'ipush'),
  G('fx_Leverage_Chest_Press', 'Machine Chest Press', 'Chest', ['Shoulders', 'Triceps'], 'Machine', ['machine'], 2, 'hpush'),
  G('fx_Dips_-_Chest_Version', 'Chest Dips', 'Chest', ['Triceps', 'Shoulders'], 'Bodyweight+', ['pullup', 'machine'], 2, 'dip', { bw: true, stress: ['shoulders'] }),
  G('fx_Pushups', 'Push-ups', 'Chest', ['Shoulders', 'Triceps', 'Core'], 'Bodyweight', [], 2, 'hpush', { bw: true, stress: ['wrists'] }),
  G('fx_Decline_Push-Up', 'Feet-Elevated Push-up', 'Chest', ['Shoulders', 'Triceps'], 'Bodyweight', [], 2, 'ipush', { bw: true, stress: ['wrists', 'shoulders'] }),
  G('x_pecdeck', 'Pec Deck', 'Chest', [], 'Machine', ['machine'], 3, 'fly'),
  G('x_cflies', 'Cable Flies', 'Chest', ['Shoulders'], 'Cable', ['cable'], 3, 'fly'),
  G('fx_Dumbbell_Flyes', 'Dumbbell Flyes', 'Chest', ['Shoulders'], 'Dumbbell', ['dumbbell', 'bench'], 3, 'fly', { stress: ['shoulders'] }),
  // Back
  G('x_dead', 'Conventional Deadlift', 'Back', ['Glutes', 'Hamstrings', 'Lower Back', 'Traps', 'Forearms'], 'Barbell', ['barbell'], 1, 'hinge', { stress: ['lowerBack', 'hips'], technical: true, heavy: true }),
  G('fx_Bent_Over_Barbell_Row', 'Bent Over Barbell Row', 'Back', ['Biceps', 'Shoulders', 'Lower Back'], 'Barbell', ['barbell'], 2, 'hpull', { stress: ['lowerBack'] }),
  G('x_pullup', 'Weighted Pull-up', 'Back', ['Biceps'], 'Bodyweight+', ['pullup'], 2, 'vpull', { bw: true, stress: ['shoulders', 'elbows'] }),
  G('fx_Chin-Up', 'Chin-up', 'Back', ['Biceps', 'Forearms'], 'Bodyweight', ['pullup'], 2, 'vpull', { bw: true, stress: ['elbows'] }),
  G('x_lat', 'Lat Pulldown (wide)', 'Back', ['Biceps'], 'Cable', ['cable'], 2, 'vpull'),
  G('fx_Close-Grip_Front_Lat_Pulldown', 'Close-Grip Lat Pulldown', 'Back', ['Biceps', 'Shoulders'], 'Cable', ['cable'], 2, 'vpull'),
  G('x_cablerow', 'Cable Row (neutral)', 'Back', ['Biceps', 'Traps'], 'Cable', ['cable'], 2, 'hpull'),
  G('fx_Leverage_Iso_Row', 'Machine Row', 'Back', ['Biceps'], 'Machine', ['machine'], 2, 'hpull'),
  G('x_csrow', 'Chest-Supported Row', 'Back', ['Biceps', 'Shoulders'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'hpull'),
  G('fx_One-Arm_Dumbbell_Row', 'One-Arm Dumbbell Row', 'Back', ['Biceps', 'Shoulders'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'hpull'),
  G('fx_Bent_Over_Two-Dumbbell_Row', 'Bent Over Dumbbell Row', 'Back', ['Biceps', 'Shoulders'], 'Dumbbell', ['dumbbell'], 2, 'hpull', { stress: ['lowerBack'] }),
  G('fx_Alternating_Kettlebell_Row', 'Kettlebell Row', 'Back', ['Biceps'], 'Kettlebell', ['kettlebell'], 2, 'hpull', { stress: ['lowerBack'] }),
  G('fx_Inverted_Row', 'Inverted Row', 'Back', ['Biceps', 'Shoulders'], 'Bodyweight', ['barbell'], 2, 'hpull', { bw: true }),
  G('fx_Straight-Arm_Pulldown', 'Straight-Arm Pulldown', 'Back', [], 'Cable', ['cable'], 3, 'pullover'),
  G('xg_bandrow', 'Band Row', 'Back', ['Biceps', 'Shoulders'], 'Band', ['bands'], 2, 'hpull', { bw: true }),
  // Shoulders
  G('x_ohp', 'Overhead Press', 'Shoulders', ['Triceps'], 'Barbell', ['barbell'], 1, 'vpush', { stress: ['shoulders', 'lowerBack'], technical: true }),
  G('x_dbohp', 'Standing DB Shoulder Press', 'Shoulders', ['Triceps'], 'Dumbbell', ['dumbbell'], 2, 'vpush', { stress: ['shoulders'] }),
  G('fx_Seated_Dumbbell_Press', 'Seated Dumbbell Press', 'Shoulders', ['Triceps'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'vpush', { stress: ['shoulders'] }),
  G('x_mohp', 'Machine Shoulder Press', 'Shoulders', ['Triceps'], 'Machine', ['machine'], 2, 'vpush', { stress: ['shoulders'] }),
  G('fx_Alternating_Kettlebell_Press', 'Kettlebell Press', 'Shoulders', ['Triceps'], 'Kettlebell', ['kettlebell'], 2, 'vpush', { stress: ['shoulders'] }),
  G('xg_pikepush', 'Pike Push-up', 'Shoulders', ['Triceps', 'Chest'], 'Bodyweight', [], 2, 'vpush', { bw: true, stress: ['shoulders', 'wrists'] }),
  G('fx_Side_Lateral_Raise', 'Dumbbell Lateral Raise', 'Shoulders', [], 'Dumbbell', ['dumbbell'], 3, 'lateral'),
  G('x_lat_r', 'Cable Lateral Raise', 'Shoulders', [], 'Cable', ['cable'], 3, 'lateral'),
  G('fx_Lateral_Raise_-_With_Bands', 'Band Lateral Raise', 'Shoulders', [], 'Band', ['bands'], 3, 'lateral'),
  G('x_facepull', 'Face Pull', 'Shoulders', ['Traps', 'Back'], 'Cable', ['cable'], 3, 'reardelt'),
  G('fx_Reverse_Flyes', 'Reverse Dumbbell Fly', 'Shoulders', ['Back'], 'Dumbbell', ['dumbbell'], 3, 'reardelt'),
  G('fx_Band_Pull_Apart', 'Band Pull-Apart', 'Shoulders', ['Back', 'Traps'], 'Band', ['bands'], 3, 'reardelt', { bw: true }),
  // Quads
  G('x_squat', 'Back Squat', 'Quads', ['Glutes', 'Hamstrings', 'Lower Back'], 'Barbell', ['barbell'], 1, 'squat', { stress: ['knees', 'lowerBack', 'hips'], technical: true, heavy: true }),
  G('x_fsquat', 'Front Squat', 'Quads', ['Glutes', 'Core'], 'Barbell', ['barbell'], 1, 'squat', { stress: ['knees', 'wrists'], technical: true }),
  G('x_hacksq', 'Hack Squat', 'Quads', ['Glutes'], 'Machine', ['machine'], 2, 'squat', { stress: ['knees'] }),
  G('x_legpress', 'Leg Press', 'Quads', ['Glutes', 'Hamstrings'], 'Machine', ['machine'], 2, 'squat', { stress: ['knees'] }),
  G('fx_Goblet_Squat', 'Goblet Squat', 'Quads', ['Glutes', 'Core'], 'Dumbbell', [], 2, 'squat', { any: ['dumbbell', 'kettlebell'], stress: ['knees'] }),
  G('x_bss', 'Bulgarian Split Squat', 'Quads', ['Glutes', 'Hamstrings'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'lunge', { stress: ['knees'] }),
  G('fx_Dumbbell_Lunges', 'Dumbbell Lunges', 'Quads', ['Glutes', 'Hamstrings'], 'Dumbbell', ['dumbbell'], 2, 'lunge', { stress: ['knees'] }),
  G('fx_Dumbbell_Step_Ups', 'Dumbbell Step-ups', 'Quads', ['Glutes', 'Hamstrings'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'lunge', { stress: ['knees'] }),
  G('fx_Bodyweight_Squat', 'Bodyweight Squat', 'Quads', ['Glutes', 'Hamstrings'], 'Bodyweight', [], 2, 'squat', { bw: true, stress: ['knees'] }),
  G('fx_Bodyweight_Walking_Lunge', 'Walking Lunge', 'Quads', ['Glutes', 'Hamstrings'], 'Bodyweight', [], 2, 'lunge', { bw: true, stress: ['knees'] }),
  G('fx_Leg_Extensions', 'Leg Extension', 'Quads', [], 'Machine', ['machine'], 3, 'legext', { stress: ['knees'] }),
  // Hamstrings / Glutes
  G('x_rdl', 'Romanian Deadlift', 'Hamstrings', ['Glutes', 'Lower Back'], 'Barbell', ['barbell'], 2, 'hinge', { stress: ['lowerBack'] }),
  G('fx_Stiff-Legged_Dumbbell_Deadlift', 'Dumbbell Romanian Deadlift', 'Hamstrings', ['Glutes', 'Lower Back'], 'Dumbbell', ['dumbbell'], 2, 'hinge', { stress: ['lowerBack'] }),
  G('fx_One-Arm_Kettlebell_Swings', 'Kettlebell Swing', 'Hamstrings', ['Glutes', 'Lower Back', 'Shoulders'], 'Kettlebell', ['kettlebell'], 2, 'hinge', { stress: ['lowerBack'] }),
  G('x_slc', 'Seated Leg Curl', 'Hamstrings', [], 'Machine', ['machine'], 3, 'legcurl'),
  G('x_llc', 'Lying Leg Curl', 'Hamstrings', [], 'Machine', ['machine'], 3, 'legcurl'),
  G('xg_slrdl', 'Single-Leg Romanian Deadlift', 'Hamstrings', ['Glutes', 'Core'], 'Bodyweight', [], 2, 'hinge', { bw: true }),
  G('x_hipthr', 'Hip Thrust', 'Glutes', ['Hamstrings'], 'Barbell', ['barbell', 'bench'], 2, 'glute'),
  G('fx_Barbell_Glute_Bridge', 'Barbell Glute Bridge', 'Glutes', ['Hamstrings'], 'Barbell', ['barbell'], 2, 'glute'),
  G('xg_dbhipthr', 'Dumbbell Hip Thrust', 'Glutes', ['Hamstrings'], 'Dumbbell', ['dumbbell', 'bench'], 2, 'glute'),
  G('fx_Single_Leg_Glute_Bridge', 'Single-Leg Glute Bridge', 'Glutes', ['Hamstrings'], 'Bodyweight', [], 3, 'glute', { bw: true }),
  G('fx_Glute_Kickback', 'Glute Kickback', 'Glutes', ['Hamstrings'], 'Bodyweight', [], 3, 'glute', { bw: true }),
  // Calves
  G('x_scalf', 'Standing Calf Raise', 'Calves', [], 'Machine', ['machine'], 3, 'calf'),
  G('x_sitcalf', 'Seated Calf Raise', 'Calves', [], 'Machine', ['machine'], 3, 'calf'),
  G('fx_Calf_Raise_On_A_Dumbbell', 'Dumbbell Calf Raise', 'Calves', [], 'Dumbbell', ['dumbbell'], 3, 'calf'),
  G('xg_bwcalf', 'Single-Leg Calf Raise', 'Calves', [], 'Bodyweight', [], 3, 'calf', { bw: true }),
  // Biceps
  G('x_ezcurl', 'EZ-Bar Curl', 'Biceps', ['Forearms'], 'Barbell', ['barbell'], 3, 'curl'),
  G('fx_Barbell_Curl', 'Barbell Curl', 'Biceps', ['Forearms'], 'Barbell', ['barbell'], 3, 'curl', { stress: ['wrists'] }),
  G('fx_Dumbbell_Bicep_Curl', 'Dumbbell Curl', 'Biceps', ['Forearms'], 'Dumbbell', ['dumbbell'], 3, 'curl'),
  G('x_hammer', 'Hammer Curl', 'Biceps', ['Forearms'], 'Dumbbell', ['dumbbell'], 3, 'curl'),
  G('fx_Incline_Dumbbell_Curl', 'Incline Dumbbell Curl', 'Biceps', [], 'Dumbbell', ['dumbbell', 'bench'], 3, 'curl'),
  G('x_preach', 'Preacher Curl', 'Biceps', [], 'Machine', ['machine'], 3, 'curl', { stress: ['elbows'] }),
  G('fx_Standing_Biceps_Cable_Curl', 'Cable Curl', 'Biceps', [], 'Cable', ['cable'], 3, 'curl'),
  G('xg_bandcurl', 'Band Curl', 'Biceps', [], 'Band', ['bands'], 3, 'curl', { bw: true }),
  // Triceps
  G('fx_Close-Grip_Barbell_Bench_Press', 'Close-Grip Bench Press', 'Triceps', ['Chest', 'Shoulders'], 'Barbell', ['barbell', 'bench'], 2, 'hpush', { stress: ['wrists', 'shoulders'] }),
  G('x_push', 'Cable Triceps Pushdown', 'Triceps', [], 'Cable', ['cable'], 3, 'triext'),
  G('x_ohext', 'Overhead Cable Extension', 'Triceps', [], 'Cable', ['cable'], 3, 'triext', { stress: ['elbows'] }),
  G('x_skull', 'Skull Crushers', 'Triceps', [], 'Barbell', ['barbell', 'bench'], 3, 'triext', { stress: ['elbows', 'wrists'] }),
  G('fx_Lying_Dumbbell_Tricep_Extension', 'Lying Dumbbell Triceps Extension', 'Triceps', [], 'Dumbbell', ['dumbbell', 'bench'], 3, 'triext', { stress: ['elbows'] }),
  G('fx_Tricep_Dumbbell_Kickback', 'Dumbbell Kickback', 'Triceps', [], 'Dumbbell', ['dumbbell'], 3, 'triext'),
  G('fx_Dips_-_Triceps_Version', 'Triceps Dips', 'Triceps', ['Chest', 'Shoulders'], 'Bodyweight+', ['pullup', 'machine'], 2, 'dip', { bw: true, stress: ['shoulders', 'elbows'] }),
  G('fx_Bench_Dips', 'Bench Dips', 'Triceps', ['Chest', 'Shoulders'], 'Bodyweight', [], 3, 'dip', { bw: true, stress: ['shoulders', 'wrists'] }),
  G('xg_diamondpush', 'Close-Grip Push-up', 'Triceps', ['Chest', 'Shoulders'], 'Bodyweight', [], 3, 'hpush', { bw: true, stress: ['wrists'] }),
  G('xg_bandpush', 'Band Triceps Pushdown', 'Triceps', [], 'Band', ['bands'], 3, 'triext', { bw: true }),
  // Core
  G('fx_Cable_Crunch', 'Cable Crunch', 'Core', [], 'Cable', ['cable'], 3, 'core'),
  G('x_hlr', 'Hanging Leg Raise', 'Core', ['Forearms'], 'Bodyweight', ['pullup'], 3, 'core', { bw: true }),
  G('fx_Pallof_Press', 'Pallof Press', 'Core', [], 'Cable', ['cable'], 3, 'core'),
  G('x_plank', 'Weighted Plank', 'Core', [], 'Bodyweight+', [], 3, 'core', { bw: true, unit: 'sec' }),
  G('fx_Dead_Bug', 'Dead Bug', 'Core', [], 'Bodyweight', [], 3, 'core', { bw: true }),
  G('x_abwheel', 'Ab Wheel Rollout', 'Core', ['Shoulders'], 'Other', ['machine'], 3, 'core', { bw: true, stress: ['lowerBack', 'shoulders'] }),
];
const LIB_BY_ID = Object.fromEntries(GEN_LIBRARY.map(e => [e.id, e]));

// Library entry → state.exercises shape (what actions.ensureExercises stores).
export function libraryExercise(e) {
  const x = { id: e.id, name: e.name, group: e.group, primary: e.primary, secondary: e.secondary, equipment: e.equipment, mechanic: e.mechanic, yt: '' };
  if (e.unit) x.unit = e.unit;
  if (e.id.startsWith('fx_')) x.catalog = true;
  return x;
}

// ——— Prescriptions ———————————————————————————————————————————————————————————

const RX = {
  strength: { 1: { sets: 4, reps: [3, 5], rpe: 8, rest: 180 }, 2: { sets: 3, reps: [5, 8], rpe: 8, rest: 150 }, 3: { sets: 3, reps: [8, 12], rpe: 8, rest: 90 } },
  hypertrophy: { 1: { sets: 3, reps: [6, 10], rpe: 8, rest: 150 }, 2: { sets: 3, reps: [8, 12], rpe: 8, rest: 120 }, 3: { sets: 3, reps: [10, 15], rpe: 9, rest: 75 } },
  general: { 1: { sets: 3, reps: [5, 8], rpe: 7, rest: 120 }, 2: { sets: 3, reps: [8, 12], rpe: 7, rest: 90 }, 3: { sets: 2, reps: [10, 15], rpe: 8, rest: 60 } },
};
const MAX_SETS = { 1: 5, 2: 4, 3: 4 };
const MIN_REST = { 1: 90, 2: 60, 3: 45 };
export const FIRST_TIME_RPE = 7;

// ——— Time math ———————————————————————————————————————————————————————————————
// A working set ≈ 3.5 s per rep + 15 s to get set (25–75 s). Warm-up sets are
// short but include loading (75 s each). Each exercise adds a 90 s changeover
// (walk, set up, adjust). The rest after a set counts except after the last set
// of an exercise, where the changeover replaces it.
const CHANGEOVER = 90;
const WARMUP_SET = 75;
export function setSeconds(item) {
  if (item.timed) return Math.max(20, item.reps) + 15;
  return Math.max(25, Math.min(75, Math.round(item.reps * 3.5 + 15)));
}
export function itemSeconds(item) {
  return item.warmups * WARMUP_SET + item.sets * setSeconds(item) + Math.max(0, item.sets - 1) * item.rest + CHANGEOVER;
}
export function estimateSeconds(items) {
  return (items || []).reduce((a, it) => a + itemSeconds(it), 0);
}

// ——— Muscle status ——————————————————————————————————————————————————————————

function rpeFactor(rpe) {
  if (rpe == null || !(rpe > 0)) return 1;
  if (rpe >= 9.5) return 1.25;
  if (rpe >= 8.5) return 1.1;
  if (rpe >= 7.5) return 1;
  return 0.8;
}

export function weeklyTarget(muscle, goal) {
  const lm = VOLUME_LANDMARKS[muscle] || { mev: 4, mav: 12 };
  if (goal === 'hypertrophy') return Math.round((lm.mev + lm.mav) / 2) || 8;
  if (goal === 'strength') return Math.max(lm.mev, 6);
  return Math.max(Math.round(lm.mev * 0.8), 4);
}

// Recovery of each muscle from its recent hard sets. A session's dose for a
// muscle = Σ sets × (1 primary | 0.5 secondary) × effort (RPE); it needs
// 24 h + 8 h per dose unit to recover (large muscles, ≤ 96 h) or 20 h + 7 h
// (small muscles, ≤ 72 h). Recovery = the least-recovered recent session.
export function muscleStatus(state, { goal = 'hypertrophy', idx } = {}, now = Date.now()) {
  const exIdx = idx || exerciseIndex(state);
  const done = completedSessions(state);
  const ws = weekStart(now);
  const out = {};
  for (const m of GEN_MUSCLES) out[m] = { muscle: m, recovery: 1, hoursSince: null, lastDose: 0, weekSets: 0, target: weeklyTarget(m, goal) };
  for (const s of done) {
    const t = s.completedAt || s.startedAt;
    if (t > now) continue;
    const age = now - t;
    const dose = {};
    for (const st of s.sets || []) {
      if (!isWorkingSet(st)) continue;
      const { primary, secondary } = musclesFor(exIdx[st.exerciseId]);
      const f = rpeFactor(st.rpe);
      primary.forEach(m => { dose[m] = (dose[m] || 0) + f; });
      secondary.forEach(m => { dose[m] = (dose[m] || 0) + 0.5 * f; });
      if (s.startedAt >= ws) {
        primary.forEach(m => { if (out[m]) out[m].weekSets += 1; });
        secondary.forEach(m => { if (out[m]) out[m].weekSets += 0.5; });
      }
    }
    for (const [m, d] of Object.entries(dose)) {
      const o = out[m];
      if (!o || d < 1) continue; // a stray half set doesn't count as "trained"
      const hours = age / HOUR;
      if (o.hoursSince == null || hours < o.hoursSince) { o.hoursSince = hours; o.lastDose = d; }
      if (age > 5 * DAY) continue;
      const need = LARGE.has(m) ? Math.min(96, 24 + 8 * d) : Math.min(72, 20 + 7 * d);
      o.recovery = Math.min(o.recovery, Math.max(0, Math.min(1, hours / need)));
    }
  }
  for (const o of Object.values(out)) {
    o.weekSets = Math.round(o.weekSets * 10) / 10;
    o.deficit = Math.round((o.target - o.weekSets) * 10) / 10;
    o.recoveryPct = Math.round(o.recovery * 100);
  }
  return out;
}

function exerciseIndex(state) {
  const idx = indexExercises(state);
  for (const e of GEN_LIBRARY) if (!idx[e.id]) idx[e.id] = e;
  return idx;
}

// ——— Helpers ————————————————————————————————————————————————————————————————

// Small deterministic hash (FNV-1a) → [0, 1).
function hash01(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return ((h >>> 0) % 100000) / 100000;
}

function haveAll(have, e) {
  if (!(e.needs || []).every(t => have.has(t))) return false;
  if (e.any && !e.any.some(t => have.has(t))) return false;
  return true;
}

// Weight step + minimum (lb) for an exercise's equipment, in the user's units.
export function loadStep(e, prefs = {}) {
  const kg = prefs.units === 'kg';
  const eq = e.equipment || '';
  if (eq === 'Barbell') {
    const plates = (prefs.plates && prefs.plates[kg ? 'kg' : 'lb']) || (kg ? [25, 20, 15, 10, 5, 2.5, 1.25] : [45, 35, 25, 10, 5, 2.5]);
    const smallest = Math.min(...plates.filter(p => p > 0));
    const bar = prefs.barWeight || (kg ? 20 / KG : 45);
    return { step: 2 * (isFinite(smallest) ? smallest : (kg ? 1.25 : 2.5)), min: kg ? bar * KG : bar, kg };
  }
  if (eq === 'Dumbbell') return { step: kg ? 2 : 5, min: kg ? 2 : 5, kg };
  if (eq === 'Kettlebell') return { step: kg ? 4 : 5, min: kg ? 8 : 10, kg };
  return { step: kg ? 2.5 : 5, min: kg ? 2.5 : 5, kg }; // machine, cable, smith
}
// Round a lb load to something loadable; returns lb (exact kg multiple for kg users).
export function roundLoadable(lb, e, prefs = {}) {
  if (!(lb > 0)) return 0;
  const { step, min, kg } = loadStep(e, prefs);
  const v = kg ? lb * KG : lb;
  const r = Math.max(min, Math.round(v / step) * step);
  return kg ? r / KG : r;
}
function fmtLoad(lb, units) {
  const v = units === 'kg' ? lb * KG : lb;
  return `${Math.abs(v - Math.round(v)) < 0.05 ? Math.round(v) : v.toFixed(1)} ${units === 'kg' ? 'kg' : 'lb'}`;
}
const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const fmtSets = (n) => String(Math.round(n * 10) / 10);

// History summary for one exercise (working sets only).
function liftHistory(state, id, now) {
  const h = exerciseHistory(state, id).filter(p => p.date <= now);
  if (!h.length) return null;
  const last = h[h.length - 1];
  const recent = h.filter(p => p.date > now - 8 * 7 * DAY).slice(-3);
  const pool = recent.length ? recent : h.slice(-3);
  const e1 = Math.max(...pool.map(p => p.e1rm || 0));
  // Best reps at bodyweight (w = 0 or bodyweight-style logging) for bw lifts.
  let bestReps = 0;
  for (const s of completedSessions(state)) {
    if (s.startedAt < now - 12 * 7 * DAY) continue;
    for (const st of s.sets || []) if (st.exerciseId === id && isWorkingSet(st) && st.reps > bestReps) bestReps = st.reps;
  }
  const exposures12 = h.filter(p => p.date > now - 12 * 7 * DAY).length;
  return { e1, last, exposures: h.length, exposures12, lastDate: last.date, daysSince: (now - last.date) / DAY, maxW: last.maxW || last.bestW, bestReps };
}

// ——— The generator ——————————————————————————————————————————————————————————

/**
 * generateWorkout(state, opts, now)
 * opts: { minutes=45, goal, focus='auto', muscles=[], equipment (tags; default from profile),
 *         include=[exerciseIds] (forced in: locks and swaps), locked=[ids] (labelled "Locked by you";
 *         defaults to every include), exclude=[exerciseIds], seed=0 }
 */
export function generateWorkout(state, opts = {}, now = Date.now()) {
  const profile = state.profile || {};
  const prefs = state.prefs || {};
  const units = prefs.units === 'kg' ? 'kg' : 'lb';
  const minutes = Math.max(10, Math.min(120, +opts.minutes || 45));
  const goal = ['strength', 'hypertrophy', 'general'].includes(opts.goal) ? opts.goal
    : profile.goal === 'strength' ? 'strength' : profile.goal === 'general' || profile.goal === 'fatloss' ? 'general' : 'hypertrophy';
  const focus = FOCUS_OPTIONS.some(f => f.id === opts.focus) ? opts.focus : 'auto';
  const focusMuscles = focus === 'muscles'
    ? (opts.muscles || []).filter(m => GEN_MUSCLES.includes(m))
    : FOCUS_OPTIONS.find(f => f.id === focus).muscles;
  const explicit = focus !== 'auto' && focus !== 'full';
  const seed = opts.seed | 0;
  const have = new Set(Array.isArray(opts.equipment) ? opts.equipment : resolveEquipment(profile));
  const injuries = injuryTags(profile.injuries);
  const avoid = new Set([...(profile.avoidExercises || []), ...(opts.exclude || [])]);
  const lockedSet = Array.isArray(opts.locked) ? new Set(opts.locked) : null;
  const include = (opts.include || []).filter((id, i, a) => a.indexOf(id) === i && !(profile.avoidExercises || []).includes(id));
  const idx = exerciseIndex(state);
  const status = muscleStatus(state, { goal, idx }, now);
  const target = minutes * 60;
  const notes = [];

  // Readiness: a run-down lifter gets a lower effort target, never more work.
  const ready = !opts.skipReadiness && completedSessions(state).length >= 4 ? readiness(state, now) : null;
  const rpeAdj = ready && ready.score != null && ready.score < 50 ? -0.5 : 0;
  if (rpeAdj) notes.push(`Readiness is low (${ready.score}), so effort targets are half an RPE lighter today.`);

  // Candidate pool: the curated library + the user's own exercises with recent history.
  const programIds = new Set();
  for (const p of state.programs || []) if (p.id === state.activeProgramId) for (const d of p.days || []) for (const pe of d.exercises || []) programIds.add(pe.exerciseId);
  const pool = GEN_LIBRARY.map(e => ({ ...e, source: 'library' }));
  for (const e of state.exercises || []) {
    if (LIB_BY_ID[e.id] || !e.id) continue;
    const g = e.group && GEN_MUSCLES.includes(e.group) ? e.group : null;
    if (!g) continue;
    const tag = { Barbell: 'barbell', 'EZ Bar': 'barbell', Dumbbell: 'dumbbell', Machine: 'machine', Smith: 'machine', Cable: 'cable', Kettlebell: 'kettlebell', Band: 'bands', Bodyweight: null, 'Bodyweight+': null }[e.equipment];
    if (tag === undefined) continue; // 'Other', foam rollers, balls: requirements unknown
    const h = exerciseHistory(state, e.id);
    if (h.length < 2) continue;
    const needs = tag ? [tag] : [];
    if (/bench|incline|decline|preacher|lying|seated/i.test(e.name) && (tag === 'dumbbell' || tag === 'barbell')) needs.push('bench');
    if (/pull-?up|chin-?up|hanging/i.test(e.name)) needs.push('pullup');
    const isComp = e.mechanic === 'compound';
    pool.push({
      id: e.id, name: e.name, group: g, primary: musclesFor(e).primary.filter(m => GEN_MUSCLES.includes(m)).length ? musclesFor(e).primary : [g],
      secondary: musclesFor(e).secondary, equipment: e.equipment, needs, tier: isComp ? 2 : 3, pattern: 'own:' + e.id,
      mechanic: e.mechanic, stress: [], bw: !tag, unit: e.unit, source: 'history',
    });
  }
  const exp = profile.experience;
  const novice = exp === 'new' || exp === 'beginner' || (!exp && completedSessions(state).length < 6);
  const histCache = {};
  const hist = (id) => (id in histCache ? histCache[id] : (histCache[id] = liftHistory(state, id, now)));

  const usable = (e) => haveAll(have, e) && !avoid.has(e.id) && !(e.stress || []).some(t => injuries.includes(t));
  const blockedByInjury = (e) => haveAll(have, e) && !avoid.has(e.id) && (e.stress || []).some(t => injuries.includes(t));

  // Muscles we may train, with a priority score.
  const muscleScore = {};
  const skipped = [];
  for (const m of focusMuscles) {
    const st = status[m];
    const minRec = opts.ignoreRecovery ? 0 : explicit ? 0.4 : 0.6;
    if (st.recovery < minRec) { skipped.push(m); continue; }
    const def = Math.max(-1, Math.min(1, st.deficit / Math.max(1, st.target)));
    muscleScore[m] = Math.pow(st.recovery, 1.5) * (1 + def) + (LARGE.has(m) ? 0.15 : 0) + (m === 'Core' ? -0.25 : 0);
  }
  if (skipped.length) notes.push(`Left out ${skipped.join(', ')}: still recovering from recent hard sets.`);

  // Slots: large muscles get up to 2 exercises, small ones 1 (+1 on long sessions).
  const slotList = [];
  for (const [m, sc] of Object.entries(muscleScore)) {
    const n = LARGE.has(m) ? 2 : (minutes >= 60 && m !== 'Core' ? 2 : 1);
    for (let k = 0; k < n; k++) slotList.push({ muscle: m, k, value: sc * (k === 0 ? 1 : 0.5) * (LARGE.has(m) || m === 'Shoulders' ? 1 : 0.8) });
  }
  slotList.sort((a, b) => b.value - a.value || (a.muscle < b.muscle ? -1 : 1));

  const chosen = [];
  const chosenIds = new Set();
  const patternsBy = {};

  const scoreCandidate = (e, muscle, k) => {
    let s = 0;
    const big = LARGE.has(muscle) || muscle === 'Shoulders';
    if (big && k === 0) s += e.tier === 1 ? 3 : e.tier === 2 ? 2.4 : 0.5;
    else if (big) s += e.tier === 3 ? 2.6 : e.tier === 2 ? 2.2 : 0.8;
    else s += e.tier === 3 ? 3 : e.tier === 2 ? 1.2 : 0;
    if (goal === 'strength' && e.tier === 1) s += 1.5;
    if (goal === 'hypertrophy' && e.tier !== 1 && !e.bw) s += 0.4;
    if (goal === 'general' && e.tier === 1) s -= 0.6;
    // New or unknown-experience lifters with little history start on easier-to-learn lifts.
    if (novice && e.technical && !hist(e.id)) s -= 1.2;
    const h = hist(e.id);
    if (h) {
      s += Math.min(2, h.exposures12 * 0.25) + 0.6;
      if (h.daysSince < 3) s -= 1.4; // variety: not the same lift two days running
    }
    if (programIds.has(e.id)) s += 0.4;
    if (patternsBy[muscle] && patternsBy[muscle].has(e.pattern)) s -= 2.5; // second chest exercise ≠ another flat press
    if (e.heavy && chosen.some(c => c.heavy)) s -= 2; // one heavy spinal lift per day
    if (seed) s += hash01(e.id + ':' + seed) * 1.6;
    return s;
  };
  const pickFor = (muscle, k, exclude = new Set()) => {
    const cands = pool.filter(e => e.primary.includes(muscle) && usable(e) && !chosenIds.has(e.id) && !exclude.has(e.id)
      && (e.unit !== 'sec' || muscle === 'Core'));
    if (!cands.length) return null;
    const ranked = cands.map(e => ({ e, s: scoreCandidate(e, muscle, k) })).sort((a, b) => b.s - a.s || (a.e.id < b.e.id ? -1 : 1));
    return ranked[0].e;
  };

  const prescribe = (e, muscle, k, forced = false) => {
    const rx = RX[goal][e.tier];
    const st = status[muscle] || status[e.primary[0]] || { recovery: 1 };
    let sets = rx.sets;
    if (st.recovery < 0.6) sets = Math.max(2, sets - 1);
    const timed = e.unit === 'sec';
    let [repMin, repMax] = rx.reps;
    const h = hist(e.id);
    if (timed) { repMin = 30; repMax = 45; }
    else if (e.bw && h && h.bestReps > 0 && !(h.e1 > 0 && h.maxW > 0)) {
      const t = Math.max(4, Math.round(h.bestReps * 0.8));
      repMin = Math.max(3, t - 2); repMax = t + 2;
    } else if (e.bw && !h) { repMin = 6; repMax = 12; }
    const reps = timed ? 40 : Math.round((repMin + repMax) / 2);
    const rpe = Math.max(6, rx.rpe + rpeAdj);

    // Load: from a recent e1RM at the target reps and RPE, never invented.
    let load = null, loadWhy = '', firstTime = false;
    const weightedHist = h && h.e1 > 0 && h.maxW > 0;
    if (timed) { load = 0; loadWhy = h ? `Hold ${repMin}–${repMax} s; add a plate on your back when it gets easy.` : `Hold ${repMin}–${repMax} s with good form.`; }
    else if (weightedHist && e.bw) {
      // Added-load lifts (weighted pull-ups, dips): keep last time's added weight.
      load = roundLoadable(h.maxW, e, prefs);
      loadWhy = `Same added weight as your last session (+${fmtLoad(load, units)}); aim for ${reps} reps at RPE ${rpe}.`;
    } else if (weightedHist) {
      let e1 = h.e1;
      let eased = '';
      if (h.daysSince > 42) { e1 *= 0.92; eased = ` It's been ${Math.round(h.daysSince / 7)} weeks, so it's eased 8%.`; }
      let raw = loadFor(e1, Math.min(reps, 12), rpe, 1);
      if (reps > 12) raw = e1 * Math.max(0.4, 0.68 - (reps - 12) * 0.027);
      // Never jump more than ~10% over the heaviest recent working weight.
      if (h.maxW > 0) raw = Math.min(raw, h.maxW * 1.1);
      load = roundLoadable(raw, e, prefs);
      loadWhy = `From your recent best (est. 1RM ${fmtLoad(roundLoadable(e1, { equipment: 'Machine' }, prefs), units)}): ${reps} reps at RPE ${rpe}.${eased}`;
    } else if (e.bw) {
      load = 0;
      loadWhy = h && h.bestReps ? `Bodyweight. Your best recent set is ${h.bestReps} rep${h.bestReps === 1 ? '' : 's'}.` : 'Bodyweight. First time: stop 2–3 reps short of failure.';
      firstTime = !h;
    } else {
      firstTime = true;
      loadWhy = `First time — find a weight at RPE ${FIRST_TIME_RPE}.`;
    }

    // Warm-ups: main lifts get a ramp, loaded compounds one feeler set.
    let warmups = 0;
    if (e.tier === 1) warmups = load > 0 ? (load >= 225 ? 3 : 2) : 2;
    else if (e.tier === 2 && !e.bw && (k === 0 || !chosen.length)) warmups = 1;
    if (chosen.length > 2 && e.tier === 2) warmups = 0; // later in the session you're already warm

    return {
      key: e.id, exerciseId: e.id, exercise: e.source === 'history' ? null : libraryExercise(e),
      name: (idx[e.id] && idx[e.id].name) || e.name, muscle, primary: e.primary, secondary: e.secondary,
      equipment: e.equipment, tier: e.tier, pattern: e.pattern, heavy: !!e.heavy,
      sets, warmups, repMin, repMax, reps, rpe, rest: rx.rest, timed, bodyweight: !!e.bw,
      load, loadWhy, firstTime, locked: forced && (!lockedSet || lockedSet.has(e.id)), why: '', history: h ? { exposures: h.exposures, daysSince: Math.round(h.daysSince) } : null,
    };
  };

  const addItem = (e, muscle, k, forced) => {
    const it = prescribe(e, muscle, k, forced);
    chosen.push(it);
    chosenIds.add(e.id);
    (patternsBy[muscle] = patternsBy[muscle] || new Set()).add(e.pattern);
    return it;
  };

  // 1) Locked / forced exercises first.
  for (const id of include) {
    const e = pool.find(x => x.id === id) || (idx[id] ? {
      id, name: idx[id].name, group: idx[id].group, primary: musclesFor(idx[id]).primary, secondary: musclesFor(idx[id]).secondary,
      equipment: idx[id].equipment, needs: [], tier: idx[id].mechanic === 'compound' ? 2 : 3, pattern: 'own:' + id, stress: [], unit: idx[id].unit, source: 'history',
    } : null);
    if (!e || chosenIds.has(id)) continue;
    const muscle = e.primary.find(m => GEN_MUSCLES.includes(m)) || e.group;
    addItem(e, muscle, (patternsBy[muscle] ? 1 : 0), true);
  }
  // Slots already covered by locked exercises.
  const usedSlots = {};
  for (const it of chosen) usedSlots[it.muscle] = (usedSlots[it.muscle] || 0) + 1;

  // 2) Fill slots in priority order while the time budget allows.
  const missing = new Set();
  for (const sl of slotList) {
    if ((usedSlots[sl.muscle] || 0) > sl.k) continue;
    const e = pickFor(sl.muscle, sl.k);
    if (!e) { if (sl.k === 0) missing.add(sl.muscle); continue; }
    const trial = prescribe(e, sl.muscle, sl.k);
    if (estimateSeconds(chosen) + itemSeconds(trial) > target * 1.04 && chosen.length) continue;
    addItem(e, sl.muscle, sl.k, false);
    usedSlots[sl.muscle] = (usedSlots[sl.muscle] || 0) + 1;
  }
  // 2b) Still short (few muscles / little equipment): third exercises for the top muscles.
  for (let round = 2; round <= 3 && estimateSeconds(chosen) < target * 0.8; round++) {
    for (const m of Object.keys(muscleScore).sort((a, b) => muscleScore[b] - muscleScore[a] || (a < b ? -1 : 1))) {
      if (estimateSeconds(chosen) >= target * 0.8) break;
      if ((usedSlots[m] || 0) >= round + 1) continue;
      const e = pickFor(m, round);
      if (!e) continue;
      const trial = prescribe(e, m, round);
      if (estimateSeconds(chosen) + itemSeconds(trial) > target * 1.04) continue;
      addItem(e, m, round, false);
      usedSlots[m] = (usedSlots[m] || 0) + 1;
    }
  }
  for (const m of missing) {
    const blocked = pool.filter(e => e.primary.includes(m) && blockedByInjury(e));
    if (blocked.length) notes.push(`No ${m.toLowerCase()} exercise that spares your ${injuries.map(t => INJURY_LABEL[t]).join(' and ')} fits your equipment.`);
    else if (!pool.some(e => e.primary.includes(m) && haveAll(have, e))) notes.push(`Nothing for ${m.toLowerCase()} matches your equipment.`);
  }

  // 3) Fit the budget: add sets (biggest deficit first), trim, then tune rest.
  const deficitOf = (it) => (status[it.muscle] ? status[it.muscle].deficit : 0);
  let guard = 0;
  while (estimateSeconds(chosen) < target * 0.95 && guard++ < 40) {
    const order = chosen.filter(it => it.sets < MAX_SETS[it.tier] + (it.bodyweight ? 1 : 0))
      .sort((a, b) => (a.sets - b.sets) || (deficitOf(b) - deficitOf(a)) || (a.key < b.key ? -1 : 1));
    const it = order.find(x => estimateSeconds(chosen) + setSeconds(x) + x.rest <= target * 1.05);
    if (!it) break;
    it.sets++;
  }
  guard = 0;
  while (estimateSeconds(chosen) > target * 1.08 && guard++ < 40) {
    const it = [...chosen].filter(x => x.sets > 2).sort((a, b) => (a.locked - b.locked) || (b.tier - a.tier) || (deficitOf(a) - deficitOf(b)) || (b.sets - a.sets))[0];
    if (it) { it.sets--; continue; }
    const drop = [...chosen].reverse().findIndex(x => !x.locked);
    if (drop < 0 || chosen.length <= 1) break;
    chosen.splice(chosen.length - 1 - drop, 1);
  }
  // Rest tuning: scale every rest by one factor (±30%) so the total lands on the budget.
  const restTotal = chosen.reduce((a, it) => a + Math.max(0, it.sets - 1) * it.rest, 0);
  const fixed = estimateSeconds(chosen) - restTotal;
  if (restTotal > 0) {
    const f = Math.max(0.7, Math.min(1.3, (target - fixed) / restTotal));
    for (const it of chosen) it.rest = Math.max(MIN_REST[it.tier], Math.round((it.rest * f) / 15) * 15);
  }

  // 4) Order: main lifts first, then compounds, then isolation; within a tier, priority.
  const prio = (it) => (muscleScore[it.muscle] || 0);
  chosen.sort((a, b) => (a.tier - b.tier) || (prio(b) - prio(a)) || (a.key < b.key ? -1 : 1));
  // Only the first exercise of the day needs a full ramp; the second loaded compound gets one feeler set.
  chosen.forEach((it, i) => {
    if (i > 0 && it.tier === 1 && it.warmups > 2) it.warmups = 2;
    if (i > 1 && it.tier === 2) it.warmups = 0;
  });

  // 5) Explanations.
  for (const it of chosen) it.why = whyLine(it, status[it.muscle], focus);

  const estSeconds = estimateSeconds(chosen);
  const name = workoutName(focus, focusMuscles, chosen);
  return {
    name, minutes, goal, focus, muscles: focusMuscles, seed, units,
    items: chosen,
    estSeconds, estMinutes: Math.round(estSeconds / 60),
    totalSets: chosen.reduce((a, it) => a + it.sets, 0),
    status, equipment: [...have], notes,
    readiness: ready && ready.score != null ? { score: ready.score, label: ready.label } : null,
  };
}

const isAre = (m) => (/s$/.test(m) ? 'are' : 'is');
function whyLine(it, st, focus) {
  if (it.locked) return 'Locked by you.';
  if (!st) return 'Fills out the session.';
  const m = it.muscle;
  const rec = st.hoursSince == null || st.hoursSince > 24 * 7
    ? `${m} ${/s$/.test(m) ? 'have' : 'has'}n't had hard sets in over a week`
    : st.recovery < 0.6 ? `${m} ${isAre(m)} only ${st.recoveryPct}% recovered, so sets are trimmed`
    : `${m} ${isAre(m)} ${st.recoveryPct >= 100 ? 'fully recovered' : `${st.recoveryPct}% recovered`} (${Math.round(st.hoursSince)} h since the last hard sets)`;
  const vol = st.weekSets <= 0 ? `no sets yet this week (target ${st.target})`
    : st.deficit >= 1 ? `${fmtSets(st.deficit)} sets under this week's target (${fmtSets(st.weekSets)} of ${st.target})`
    : st.deficit > -2 ? `on track for this week (${fmtSets(st.weekSets)} of ${st.target} sets)`
    : `already past this week's target (${fmtSets(st.weekSets)} of ${st.target}), so this is a top-up`;
  return `${rec}; ${vol}.`;
}

function workoutName(focus, focusMuscles, items) {
  const f = FOCUS_OPTIONS.find(x => x.id === focus);
  if (focus === 'muscles') {
    const ms = focusMuscles.slice(0, 3);
    return ms.length ? ms.join(ms.length === 2 ? ' & ' : ', ') + (focusMuscles.length > 3 ? ' +' : '') : 'Custom workout';
  }
  if (focus !== 'auto') return f.id === 'full' ? 'Full body' : f.id === 'upper' ? 'Upper body' : f.id === 'lower' ? 'Lower body' : f.label;
  const ms = [...new Set(items.map(i => i.muscle))];
  if (ms.length && ms.every(m => UPPER.has(m))) return 'Upper body';
  if (ms.length && ms.every(m => LOWER.has(m) || m === 'Core')) return 'Lower body';
  return 'Full body';
}

// Ranked alternatives for one exercise in a plan (same muscle, usable equipment).
export function alternativesFor(state, plan, exerciseId, opts = {}, now = Date.now()) {
  const item = plan.items.find(i => i.exerciseId === exerciseId);
  if (!item) return [];
  const taken = new Set(plan.items.map(i => i.exerciseId));
  const out = [];
  let seed = 0;
  const exclude = new Set([...(opts.exclude || []), ...taken]);
  // Re-run the picker with a single-muscle focus and the plan's other picks excluded.
  for (let guard = 0; guard < 6 && out.length < (opts.limit || 5); guard++) {
    const p = generateWorkout(state, {
      ...opts, focus: 'muscles', muscles: [item.muscle], minutes: 20, include: [], exclude: [...exclude], seed,
      ignoreRecovery: true, skipReadiness: true,
    }, now);
    const pick = p.items.find(i => i.muscle === item.muscle && !exclude.has(i.exerciseId));
    if (!pick) break;
    out.push({ ...pick, sets: item.sets, rest: item.rest });
    exclude.add(pick.exerciseId);
    seed = 0;
  }
  return out;
}

// Plan → session slots (session_model shape). Generated rows carry the planned
// load/reps/RPE, so the live screen prefills them (progression.planSuggestion),
// and the why-line shows as the exercise note.
export function planToSlots(plan, makeId = (i) => 'se_g' + i) {
  const exercises = [];
  const slots = plan.items.map((it, i) => {
    if (it.exercise) exercises.push(it.exercise);
    const rir = Math.max(0, Math.round(10 - it.rpe));
    const work = Array.from({ length: it.sets }, () => (
      it.load > 0 ? { type: 'normal', w: it.load, reps: it.reps, rpe: it.rpe, rir } : { type: 'normal' }
    ));
    const warm = Array.from({ length: it.load > 0 || it.firstTime ? it.warmups : 0 }, () => ({ type: 'warmup' }));
    return {
      id: makeId(i), fromProgram: false,
      exerciseId: it.exerciseId, name: it.name, group: it.muscle, unit: it.timed ? 'sec' : 'reps',
      yt: '', alternates: [],
      repMin: it.repMin, repMax: it.repMax, rest: it.rest,
      targetWeight: it.load > 0 ? it.load : 0, rpeTarget: it.rpe,
      notes: it.firstTime && !it.bodyweight ? `${it.why} First time: find a weight at RPE ${FIRST_TIME_RPE}.` : it.why,
      rows: [...warm, ...work],
      supersetId: null,
      ...(it.load > 0 ? { scheme: 'generated' } : {}),
    };
  });
  return { slots, exercises };
}

/*
Plan shape:
{ name, minutes, goal, focus, muscles[], seed, units,
  items: [{ key, exerciseId, exercise (state-shaped, or null if already in the library), name, muscle,
            primary[], secondary[], equipment, tier, sets, warmups, repMin, repMax, reps, rpe, rest (s),
            timed, bodyweight, load (lb | 0 bodyweight | null unknown), loadWhy, firstTime, locked, why }],
  estSeconds, estMinutes, totalSets, status (muscleStatus), equipment[], notes[], readiness }
*/
